import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { useNodeExport } from "@/hooks/useNodeExport";
import TabReportPrint from "@/components/shared/TabReportPrint";
import TabLoadError from "@/components/shared/TabLoadError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ListChecks, Loader2, Sparkles, Search, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

const REMARK_PLUGINS = [remarkGfm];

function statusBadge(status) {
  if (status === "won") return <Badge className="bg-green-600 hover:bg-green-600">Won</Badge>;
  if (status === "lost") return <Badge variant="secondary">Lost</Badge>;
  return <Badge variant="outline">Open</Badge>;
}

function riskCell(risk) {
  if (risk === null || risk === undefined) return <span className="text-muted-foreground">—</span>;
  const color = risk >= 66 ? "text-red-600" : risk >= 33 ? "text-amber-600" : "text-green-600";
  return <span className={`font-medium ${color}`}>{risk}</span>;
}

function SortHead({ label, k, align, sortKey, onSort }) {
  return (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => onSort(k)}>
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === k ? "text-primary" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}

export default function Deals({ onRegisterExport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [sortKey, setSortKey] = useState("amount");
  const [sortDir, setSortDir] = useState("desc");
  const printRef = useRef(null);

  const { exportPdf, printNode } = useNodeExport(
    printRef,
    () => !!(data && data.deals && data.deals.length),
    "salestap-deals"
  );

  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("deals", { exportPdf, printFn: printNode });
  }, [onRegisterExport, exportPdf, printNode, data]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [d, rep] = await Promise.all([
        api.getDeals(),
        api.getDealsReport().catch(() => ({ report_md: null })),
      ]);
      setData(d);
      setReport(rep?.report_md || null);
    } catch (e) {
      logError("Failed to load deals:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setReportLoading(true);
    try {
      await api.generateDealsReport();
      const t0 = Date.now();
      const poll = async () => {
        const st = await api.dealsReportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setReportLoading(false);
          toast.success("Deal analysis complete.");
          return;
        }
        if (st.status === "error") {
          setReportLoading(false);
          toast.error(st.error || "Analysis failed.");
          return;
        }
        if (Date.now() - t0 > 180000) {
          setReportLoading(false);
          toast.error("Analysis timed out.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReportLoading(false);
      toast.error(e.response?.data?.detail || "Analysis failed.");
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const facets = data?.facets || { owners: [], statuses: ["open", "won", "lost"] };

  const filtered = useMemo(() => {
    let rows = data?.deals || [];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.owner || "").toLowerCase().includes(q));
    if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
    if (ownerFilter !== "all") rows = rows.filter((r) => r.owner === ownerFilter);
    const dir = sortDir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return rows;
  }, [data, search, statusFilter, ownerFilter, sortKey, sortDir]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return <TabLoadError onRetry={load} />;
  }

  const total = data?.total || 0;
  const subtitle = `${fmtNumber(total)} deals · showing ${fmtNumber(filtered.length)}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Deals</h2>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <Button data-testid={TID.genDealsReportBtn} onClick={generate} disabled={reportLoading || !total} variant="outline" size="sm">
          {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {report ? "Regenerate" : "AI Risk Review"}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            data-testid={TID.dealsSearchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search deals or owners…"
            className="w-[220px] h-9 pl-8"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-9" data-testid={TID.dealsStatusFilter}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {facets.statuses.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue placeholder="Owner" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {facets.owners.map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {total === 0 ? (
        <div className="text-center py-16 text-muted-foreground">No deals found.</div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto" data-testid={TID.dealsTable}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead label="Deal" k="name" sortKey={sortKey} onSort={toggleSort} />
                    <SortHead label="Amount" k="amount" align="right" sortKey={sortKey} onSort={toggleSort} />
                    <TableHead>Stage</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <SortHead label="Age" k="age_days" align="right" sortKey={sortKey} onSort={toggleSort} />
                    <SortHead label="Risk" k="risk" align="right" sortKey={sortKey} onSort={toggleSort} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice(0, 300).map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium text-foreground max-w-[240px]">
                        <span className="truncate block">{d.name}</span>
                        {d.overdue && <span className="text-[10px] text-red-600 font-medium">overdue</span>}
                      </TableCell>
                      <TableCell className="text-right">{fmtCurrency(d.amount)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{d.stage}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{d.owner}</TableCell>
                      <TableCell>{statusBadge(d.status)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{d.age_days != null ? `${d.age_days}d` : "—"}</TableCell>
                      <TableCell className="text-right">{riskCell(d.risk)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filtered.length > 300 && (
                <p className="text-xs text-muted-foreground mt-2 text-center">Showing first 300 of {fmtNumber(filtered.length)} matching deals.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {report && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> AI Deal Risk Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <article data-testid={TID.dealsReportContent} className="prose prose-slate dark:prose-invert max-w-none prose-h2:text-lg prose-strong:text-foreground">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
            </article>
          </CardContent>
        </Card>
      )}

      <TabReportPrint ref={printRef} title="Deals" subtitle={subtitle} reportMd={report}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #0f172a" }}>
              <th style={{ textAlign: "left", padding: 4 }}>Deal</th>
              <th style={{ textAlign: "right", padding: 4 }}>Amount</th>
              <th style={{ textAlign: "left", padding: 4 }}>Stage</th>
              <th style={{ textAlign: "left", padding: 4 }}>Owner</th>
              <th style={{ textAlign: "left", padding: 4 }}>Status</th>
              <th style={{ textAlign: "right", padding: 4 }}>Risk</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 60).map((d) => (
              <tr key={d.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                <td style={{ padding: 4 }}>{d.name}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{fmtCurrency(d.amount)}</td>
                <td style={{ padding: 4 }}>{d.stage}</td>
                <td style={{ padding: 4 }}>{d.owner}</td>
                <td style={{ padding: 4, textTransform: "capitalize" }}>{d.status}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{d.risk ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabReportPrint>
    </div>
  );
}
